import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCurrencies } from '../../currencies/api/useCurrencies';
import { ErrorMessage } from '../../../shared/ui/ErrorMessage';
import { Loading } from '../../../shared/ui/Loading';
import { loadContactOpenDebts, summarizeContactBalance } from '../lib/contactBalance';

export function SideBySideDebtsPanel({ contactId }: { contactId: string }) {
  const { t } = useTranslation();
  const debts = useQuery({
    queryKey: ['contact', contactId, 'debts'],
    queryFn: () => loadContactOpenDebts(contactId),
  });
  const currencies = useCurrencies();

  if (debts.isLoading || currencies.isLoading) return <Loading />;
  if (debts.error) return <ErrorMessage error={debts.error} />;
  if (currencies.error) return <ErrorMessage error={currencies.error} />;

  const summaries = summarizeContactBalance(
    debts.data?.receivables ?? [],
    debts.data?.payables ?? [],
  );
  const currencyCode = (id: string) =>
    currencies.data?.find((currency) => currency.id === id)?.code ?? id.slice(0, 3);

  return (
    <div className="debts-panel">
      <p className="debts-panel__note" role="note">
        {t('debts.balance_explanation')}
      </p>
      {summaries.length === 0 ? <p>{t('debts.no_open_balance')}</p> : null}
      <div className="debts-panel__summary-list">
        {summaries.map((item) => (
          <details key={item.currencyId} className="debts-panel__summary">
            <summary>
              <span>{currencyCode(item.currencyId)}</span>
              <strong className="debts-panel__balance" dir="ltr">
                {item.balance.isPositive() ? '+' : ''}
                {item.balance.toFixed(2)} {currencyCode(item.currencyId)}
              </strong>
              <span className="debts-panel__expand">{t('debts.show_details')}</span>
            </summary>
            <div className="debts-panel__details">
              <section aria-label={t('debts.tab_receivables')}>
                <h3>
                  {t('debts.receivables_owed_to_us')}: {item.receivable.toFixed(2)}{' '}
                  {currencyCode(item.currencyId)}
                </h3>
                {item.receivables.map((debt) => (
                  <Link
                    key={debt.id}
                    to={`/debts/receivables/${debt.id}/receive`}
                    className="debts-panel__operation"
                  >
                    <span>{new Date(debt.createdAt).toLocaleDateString()}</span>
                    <span>
                      {debt.outstandingAmount} {currencyCode(debt.currencyId)}
                    </span>
                  </Link>
                ))}
              </section>
              <section aria-label={t('debts.tab_payables')}>
                <h3>
                  {t('debts.payables_owed_by_us')}: {item.payable.toFixed(2)}{' '}
                  {currencyCode(item.currencyId)}
                </h3>
                {item.payables.map((debt) => (
                  <Link
                    key={debt.id}
                    to={`/debts/payables/${debt.id}/pay`}
                    className="debts-panel__operation"
                  >
                    <span>{new Date(debt.createdAt).toLocaleDateString()}</span>
                    <span>
                      {debt.outstandingAmount} {currencyCode(debt.currencyId)}
                    </span>
                  </Link>
                ))}
              </section>
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}
